import { useState, useContext, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { TextField } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Overview page components
import Header from "layouts/profile/components/Header";

import PropTypes from "prop-types";
import { UserContext } from "context/UserContext";
import enums from "enums/BaseEnum";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const InfoItem = ({ label, value, isMultiline = false }) => (
  <Grid item xs={12} md={6}>
    <MDTypography
      variant="button"
      color="text"
      display="block"
      sx={{ lineHeight: 1.6 }}
      noWrap={!isMultiline}
    >
      <strong>{label}:</strong> {value || "-"}
    </MDTypography>
  </Grid>
);

const SectionCard = ({ title, children }) => (
  <Card sx={{ p: 3, height: "100%" }}>
    <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize" mb={title ? 2 : 0}>
      {title}
    </MDTypography>
    {children}
  </Card>
);

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isMultiline: PropTypes.bool,
};

SectionCard.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

/**
 * Overview component renders the profile overview layout.
 * It includes the DashboardLayout, DashboardNavbar, Header, and Footer components.
 * Inside the Header, it displays PlatformSettings, ProfileInfoCard, and ProfilesList components.
 *
 * @component
 * @returns {JSX.Element} The rendered Overview component.
 */
function Overview() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { user, loading: userLoading, changePassword, refreshUser } = useContext(UserContext);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const extractApiMessage = (payload) => {
    if (!payload) return "";
    if (typeof payload === "string") return payload;
    if (Array.isArray(payload.warnings) && payload.warnings.length > 0) {
      return payload.warnings.join(", ");
    }
    if (typeof payload.message === "string" && payload.message.trim().length > 0) {
      return payload.message;
    }
    if (payload.response) {
      const nested = extractApiMessage(payload.response.data);
      if (nested) return nested;
    }
    if (payload.data) {
      const nested = extractApiMessage(payload.data);
      if (nested) return nested;
    }
    return "";
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Lütfen yeni parolayı ve tekrarını doldurun", { position: "top-right" });
      return;
    }

    if (!isManager && !currentPassword.trim()) {
      toast.error("Lütfen eski parolanızı girin", { position: "top-right" });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Yeni şifreler eşleşmiyor!", { position: "top-right" });
      return;
    }
    const data = await changePassword(currentPassword, newPassword, confirmPassword);
    const apiMessage = extractApiMessage(data.data);
    if (data.success) {
      toast.success(apiMessage || "Parola başarıyla değiştirildi.", { position: "top-right" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      toast.error(apiMessage || "Parola değiştirilemedi.", { position: "top-right" });
    }
  };

  // Kullanıcı yükleniyorsa loading göster
  if (userLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h6" color="text">
            Yükleniyor...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  // Kullanıcı yoksa sign-in'e yönlendir
  if (!user) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h6" color="error">
            Kullanıcı bilgileri yüklenemedi. Lütfen tekrar giriş yapın.
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const roles = Array.isArray(user.roles) ? user.roles : [];
  const readableRoles = roles.map(
    (role) => enums[role] || (role === "ROLE_CUSTOMER" ? "Müşteri" : role)
  );
  const isManager = roles.includes("ROLE_MANAGER");
  const isCustomer = roles.includes("ROLE_CUSTOMER");
  const customerDetails =
    user.customerDetails && typeof user.customerDetails === "object" ? user.customerDetails : null;
  const customerFactories =
    customerDetails && Array.isArray(customerDetails.fabrikalar) ? customerDetails.fabrikalar : [];
  const tradeName = user.customerTradeName || customerDetails?.ticariUnvan || "";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header user={user}>
        <MDBox mt={5} mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <MDTypography variant="h5" fontWeight="medium">
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "-"}
                </MDTypography>
                {isCustomer && tradeName && (
                  <MDTypography variant="button" color="text">
                    {tradeName}
                  </MDTypography>
                )}
                <MDBox mt={2}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {readableRoles.map((role) => (
                      <Chip key={role} label={role} color="info" variant="filled" size="small" />
                    ))}
                  </Stack>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <SectionCard title="Kullanıcı Bilgileri">
                <Grid container spacing={2}>
                  {!isCustomer && <InfoItem label="Kullanıcı Adı" value={user.username} />}
                  <InfoItem label={enums.EMAIL} value={user.email} />
                  <InfoItem label={enums.PHONE} value={user.phone} />
                </Grid>
              </SectionCard>
            </Grid>

            {isCustomer && customerDetails && (
              <Grid item xs={12}>
                <SectionCard title="Müşteri Bilgileri">
                  <Grid container spacing={2}>
                    <InfoItem label="Ticari Ünvan" value={customerDetails.ticariUnvan} />
                    {customerDetails.vergiDairesi && (
                      <InfoItem label="Vergi Dairesi" value={customerDetails.vergiDairesi} />
                    )}
                    {customerDetails.vergiNumarasi && (
                      <InfoItem label="Vergi Numarası" value={customerDetails.vergiNumarasi} />
                    )}
                    {customerDetails.sector && (
                      <InfoItem label="Sektör" value={customerDetails.sector} />
                    )}
                    {customerDetails.eposta && (
                      <InfoItem label="E-posta" value={customerDetails.eposta} />
                    )}
                    {customerDetails.telefon && (
                      <InfoItem label="Telefon" value={customerDetails.telefon} />
                    )}
                    {customerDetails.adres && (
                      <InfoItem label="Adres" value={customerDetails.adres} isMultiline />
                    )}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {isCustomer && customerFactories.length > 0 && (
              <Grid item xs={12}>
                <SectionCard title="Fabrikalar">
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    {customerFactories.map((factory, index) => {
                      const factoryData =
                        factory && typeof factory === "object"
                          ? factory
                          : { fabrikaAdi: factory?.toString() };
                      return (
                        <MDBox
                          key={`${factoryData.fabrikaAdi || "factory"}-${index}`}
                          p={2}
                          borderRadius={2}
                          sx={{
                            backgroundColor: "grey.100",
                            border: "1px solid",
                            borderColor: "grey.200",
                          }}
                        >
                          <MDTypography variant="subtitle2" fontWeight="medium">
                            Fabrika {index + 1}: {factoryData.fabrikaAdi || "-"}
                          </MDTypography>
                          <MDTypography variant="button" color="text" display="block">
                            <strong>Sorumlu:</strong> {factoryData.fabrikaSorumlusu || "-"}
                          </MDTypography>
                          <MDTypography variant="button" color="text" display="block">
                            <strong>Telefon:</strong> {factoryData.fabrikaTelefon || "-"}
                          </MDTypography>
                          {factoryData.adres && (
                            <MDTypography
                              variant="button"
                              color="text"
                              display="block"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              <strong>Adres:</strong> {factoryData.adres}
                            </MDTypography>
                          )}
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </SectionCard>
              </Grid>
            )}

            <Grid item xs={12}>
              <SectionCard title="Şifre Güncelle">
                <MDBox component="form" onSubmit={handlePasswordUpdate}>
                  <MDBox mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Mevcut Şifre"
                      variant="outlined"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </MDBox>

                  <MDBox mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Yeni Şifre"
                      variant="outlined"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </MDBox>

                  <MDBox mb={2}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Yeni Şifre (Tekrar)"
                      variant="outlined"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </MDBox>

                  <MDButton variant="gradient" color="info" type="submit" fullWidth>
                    Güncelle
                  </MDButton>
                </MDBox>
              </SectionCard>
            </Grid>
          </Grid>
        </MDBox>
      </Header>
    </DashboardLayout>
  );
}

export default Overview;
